import type { TaskListOutput } from "@orsane/schema/task-lists";
import type { UserOutput } from "@orsane/schema/users";
import { useNavigate } from "@tanstack/react-router";
import { Ellipsis, Link, LogOut, Share, Trash2, UserPlus } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { InvitePopover } from "@/components/InvitePopover";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { authClient } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { useRemoveTaskListUserMutation } from "@/mutations/task-list-mutations";
import { useTaskListUsers } from "@/queries/list-queries";
import { inboxRoute } from "@/routes/inboxRoute";
import { UserAvatar } from "./UserAvatar";

function Person({ taskList, user, isOwner }: { taskList: TaskListOutput; user: UserOutput; isOwner: boolean }) {
    const { mutateAsync: removeMember } = useRemoveTaskListUserMutation();
    const session = authClient.useSession();
    const navigate = useNavigate();
    const activeMember = authClient.useActiveMember();
    const isSelf = user.id === session.data?.user.id;
    // Removing someone else belongs to the list's creator, like archiving and
    // deleting, and to the team's owners and admins. Removing yourself is
    // leaving, which any member may do. The server enforces both — this only
    // keeps the menu from offering what it would refuse.
    const isTeamAdmin = activeMember.data?.role === "owner" || activeMember.data?.role === "admin";
    const canRemove = isSelf || isTeamAdmin || taskList.createdById === session.data?.user.id;
    const handleRemoveMember = async () => {
        try {
            await removeMember({
                id: taskList.id,
                userId: user.id,
            });
        } catch (error) {
            // The server refuses to remove the last member — a list without
            // members is invisible to everyone while its tasks stay behind.
            toast.error(isSelf ? "Could not leave list" : "Could not remove member", {
                description: error instanceof Error ? error.message : "Please try again.",
            });
            return;
        }

        // if you remove your own from a list, redirect to the inbox
        if (isSelf) {
            navigate({ to: inboxRoute.to });
        }
    };

    const name = user.name;
    const email = user.email;

    return (
        <div className="flex items-center gap-2">
            <UserAvatar user={user} size="md" />
            <div className="mr-auto">
                <div className=" font-medium">{name}</div>
                <div className="text-sm text-orsane-muted leading-3.5">{email}</div>
            </div>
            {isOwner && <div className="pr-2 text-sm text-orsane-muted">Owner</div>}
            {!isOwner && canRemove && (
                <DropdownMenu>
                    <DropdownMenuTrigger
                        render={
                            <Button variant="ghost" size="icon" className="ml-auto">
                                <Ellipsis className="size-4 text-orsane-muted" />
                            </Button>
                        }
                    ></DropdownMenuTrigger>
                    <DropdownMenuContent side="bottom" align="end">
                        <DropdownMenuItem onClick={handleRemoveMember}>
                            {isSelf ? <LogOut className="mr-2 size-4" /> : <Trash2 className="mr-2 size-4" />}
                            {isSelf ? "Leave list" : "Remove"}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );
}

function PersonList({
    title,
    taskList,
    users,
    className,
}: {
    title: string;
    taskList: TaskListOutput;
    users: UserOutput[] | undefined;
    className?: string;
}) {
    if (users?.length === 0) {
        return null;
    }

    return (
        <div className={cn(className)}>
            <h6 className="text-sm text-zinc-500 mb-3">{title}</h6>
            <div className="flex flex-col gap-3">
                {users!.map((user) => (
                    <Person user={user} key={`${title}_${user.id}`} taskList={taskList} isOwner={taskList.createdById === user.id} />
                ))}
            </div>
        </div>
    );
}

export default function SharePopover({ taskList }: { taskList: TaskListOutput }) {
    const [open, setOpen] = useState(false);
    const { data: taskListUsers } = useTaskListUsers(taskList.id);
    const inviteInputRef = useRef<HTMLInputElement>(null);
    const handleCopyLink = () => {
        const url = `${window.location.origin}/lists/${taskList.id}`;
        navigator.clipboard.writeText(url);
        toast.success("Link copied", {
            description: `The link to this list has been copied to your clipboard.`,
        });
    };

    // const handleGuestsVisibleChange = (checked: boolean) => {
    //     updateGuestsVisible(checked);
    // };

    const handleOpenChange = (open: boolean) => {
        setOpen(open);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange}>
            <PopoverTrigger
                render={
                    <Button variant="ghost" className="text-base" disabled={taskList.isArchived}>
                        <Share className="w-4 h-4" />
                        Share
                    </Button>
                }
            />
            <PopoverContent initialFocus={inviteInputRef} side="bottom" align="end" alignOffset={-40} className="p-0 w-[380px] gap-0!">
                <div className="border-b flex items-center gap-2 px-4 py-1">
                    <UserPlus className="size-4 text-zinc-600" />
                    <InvitePopover ref={inviteInputRef} selectedUsers={taskListUsers || []} taskListId={taskList.id} />
                </div>
                {/* <div className="border-b flex items-center gap-2 px-4 py-4">
                    <Globe className="size-4 text-zinc-600" />
                    <div className=" text-zinc-600 ml-2 mr-auto">Anyone with the link can view this list</div>
                    <Switch checked={taskList.guestsVisible} onCheckedChange={handleGuestsVisibleChange} />
                </div> */}
                <div className="p-3">
                    <PersonList title="Members" taskList={taskList} users={taskListUsers} className="mb-4" />
                    {/* <PersonList title="Guests" taskList={taskList} users={[]} showAvatar={false} /> */}
                </div>
                <div className="bg-neutral-50 border-t py-2 px-3 flex justify-end rounded-b-md">
                    <Button variant="ghost" onClick={handleCopyLink}>
                        <Link />
                        Copy link
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
